import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeStatus } from '../../utils';
import { WorkflowStatus } from '../../Redux/activityModels';
import ActivityCopyableChip from './ActivityCopyableChip';

// Constants
const COPY_FEEDBACK_DURATION_MS = 2000;

export interface ActivityDebugTagsProps {
  correlationId?: string;
  taskId?: string;
  status: string;
}

type CopiedFieldType = 'correlation' | 'task' | null;

const ActivityDebugTags: React.FC<ActivityDebugTagsProps> = ({ correlationId, taskId, status }) => {
  const [copiedField, setCopiedField] = useState<CopiedFieldType>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();

  // Check if status is completed or skipped - nothing to debug once terminal-and-fine
  const normalizedStatus = normalizeStatus(status);
  const isTerminalWithoutDebug = normalizedStatus === WorkflowStatus.Success || normalizedStatus === WorkflowStatus.Skipped;
  const shouldShow = !isTerminalWithoutDebug && (correlationId || taskId);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Generic copy handler
  const handleCopy = useCallback((label: string, value: string, field: CopiedFieldType) => {
    return () => {
      if (!value) return;

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const copyText = `${label} : ${value}`;
      navigator.clipboard.writeText(copyText);
      setCopiedField(field);
      
      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        setCopiedField(null);
        timeoutRef.current = null;
      }, COPY_FEEDBACK_DURATION_MS);
    };
  }, []);

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="tw-flex tw-flex-wrap tw-gap-3" data-testid="activity-debug-tags">
      {correlationId && (
        <ActivityCopyableChip
          label={t('activityLogCorrelationId')}
          isCopied={copiedField === 'correlation'}
          onClick={handleCopy(t('activityLogCorrelationId'), correlationId, 'correlation')}
          testId="correlation-id-chip"
        />
      )}
      {taskId && (
        <ActivityCopyableChip
          label={t('activityLogTaskId')}
          isCopied={copiedField === 'task'}
          onClick={handleCopy(t('activityLogTaskId'), taskId, 'task')}
          testId="task-id-chip"
        />
      )}
    </div>
  );
};

export default React.memo(ActivityDebugTags);
